/**
 * Klient realtime kanala (SSE).
 *
 * Zamerne to NIE JE React hook, ale modulovy singleton s refcountom. Prave to
 * robi StrictMode mount -> unmount -> mount neskodnym: druhy mount pouzije uz
 * bezuce spojenie a pad refcountu na nulu sa odklada o sekundu.
 *
 * Streamom chodi LEN signal "nieco sa zmenilo". Obrazovky si na nej spustia
 * svoj existujuci fetch znova.
 */
import axios from "axios";
import { REALTIME_TICKET_URL, REALTIME_STREAM_URL } from "./UrlHelper";
import * as leader from "./realtimeLeader";
import { getAuthToken } from "./tenantStorage";

const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000];
const ZLYHANI_DO_POLLINGU = 3;
const SKUS_SSE_ZNOVA_MS = 5 * 60 * 1000;
const WATCHDOG_MS = 60000;
const WATCHDOG_KONTROLA_MS = 10000;
const ODKLAD_ODPOJENIA_MS = 1000;

let refcount = 0;
let generacia = 0; // chrani pred dobehnutim zastaraleho ticket fetchu
let es = null;
let stav = "idle"; // idle | connecting | open | polling | expired | off
let odberatelia = [];
let odberateliaStavu = [];

let pokusov = 0;
let reconnectTimer = null;
let pollTimer = null;
let skusSseTimer = null;
let watchdogTimer = null;
let odpojitTimer = null;
let poslednaSprava = 0;
let pollMs = 30000;
let pouzivaLidra = false;
// Cisto diagnosticke -- aby sa dalo zistit, PRECO tab skoncil na pollingu.
let poslednyRezim = null;
let poslednaChyba = null;

// Token aktualnej prevadzky; do poziadavky na listok ho prida request
// interceptor, tu sa len overuje, ci je vobec kto prihlaseny.
const token = () => getAuthToken();

function nastavStav(novy) {
  if (stav === novy) return;
  stav = novy;
  for (const fn of odberateliaStavu) {
    try {
      fn(novy);
    } catch (err) {
      console.error(err);
    }
  }
}

function emit(event, { rozposlat = true } = {}) {
  poslednaSprava = Date.now();
  // Lider je jediny, kto ma otvoreny stream -- ostatne zalozky ziju z tohto
  // rozposlania. `rozposlat: false` pouzivaju udalosti, ktore uz z kanala prisli.
  if (rozposlat && pouzivaLidra && leader.isLeader()) leader.broadcast(event);
  for (const fn of odberatelia) {
    try {
      fn(event);
    } catch (err) {
      console.error(err);
    }
  }
}

/**
 * Syntetická udalost "mozno sme nieco zmeskali". Prejde kazdym filtrom, takze
 * kazda obrazovka ma pre dobiehanie jedinu cestu kodu -- a polling fallback sa
 * tym zmensi na "emituj resync kazdych N sekund".
 */
function resync(dovod) {
  emit({ v: 1, entity: "resync", reason: dovod, ts: Date.now() });
}

export function subscribe(fn) {
  odberatelia.push(fn);
  return () => {
    odberatelia = odberatelia.filter((f) => f !== fn);
  };
}

export function subscribeState(fn) {
  odberateliaStavu.push(fn);
  return () => {
    odberateliaStavu = odberateliaStavu.filter((f) => f !== fn);
  };
}

export function getState() {
  return stav;
}

export function forceResync() {
  resync("manual");
}

function zrusCasovace() {
  clearTimeout(reconnectTimer);
  clearInterval(pollTimer);
  clearTimeout(skusSseTimer);
  clearInterval(watchdogTimer);
  reconnectTimer = null;
  pollTimer = null;
  skusSseTimer = null;
  watchdogTimer = null;
}

function zavriStream() {
  if (es) {
    try {
      es.close();
    } catch (err) {
      // uz je zatvoreny
    }
    es = null;
  }
}

function spustiWatchdog() {
  clearInterval(watchdogTimer);
  // Dominantny sposob zlyhania SSE je TICHO mrtvy socket, kde sa onerror nikdy
  // nespusti. Heartbeat zo servera + tento watchdog su jediny spolahlivy
  // detektor.
  //
  // Heartbeat MUSI zo servera chodit ako skutocna sprava (`data:`), nie ako SSE
  // komentar (`: hb`) -- na komentare sa `onmessage` vobec nespusti, takze by
  // tento watchdog kazdu minutu zhodil uplne zdravy stream.
  //
  // Na skrytom tabe sa bezne nekontroluje -- tam je ticho v poriadku. Lider je
  // vynimka: zivia sa z neho ostatne zalozky, takze keby v pozadi prestal
  // kontrolovat, viditelny follower by o mrtvom streame nevedel. (Ak tab OS
  // uplne zmrazi, prestane obnovovat aj lease a lidrovstvo po 5 s prevezme
  // niekto iny.)
  watchdogTimer = setInterval(() => {
    const somLiderPreOstatnych = pouzivaLidra && leader.isLeader();
    if (!somLiderPreOstatnych && document.visibilityState !== "visible") return;
    if (Date.now() - poslednaSprava < WATCHDOG_MS) return;
    console.warn("[realtime] watchdog: stream nevysiela, pripajam znova");
    zavriStream();
    naplanujReconnect(true);
  }, WATCHDOG_KONTROLA_MS);
}

function prepniNaPolling() {
  zavriStream();
  zrusCasovace();
  nastavStav("polling");
  pollTimer = setInterval(() => resync("poll"), pollMs);
  // SSE sa z casu na cas skusi znova -- strop na serveri mohol medzitym
  // uvolnit miesto, alebo sa opravila siet.
  skusSseTimer = setTimeout(() => {
    pokusov = 0;
    pripoj();
  }, SKUS_SSE_ZNOVA_MS);
}

function naplanujReconnect(hned = false) {
  if (refcount === 0 || reconnectTimer) return;
  if (pouzivaLidra && !leader.isLeader()) return;

  if (pokusov >= ZLYHANI_DO_POLLINGU) {
    prepniNaPolling();
    return;
  }

  const zaklad = hned ? 0 : BACKOFF_MS[Math.min(pokusov, BACKOFF_MS.length - 1)];
  const jitter = Math.floor(Math.random() * zaklad * 0.2);
  pokusov += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    pripoj();
  }, zaklad + jitter);
}

async function pripoj() {
  if (refcount === 0) return;
  if (es) return;
  if (!token()) return;
  if (pouzivaLidra && !leader.isLeader()) return;

  const mojaGeneracia = ++generacia;
  nastavStav("connecting");

  let odpoved;
  try {
    const res = await axios.post(REALTIME_TICKET_URL, {});
    odpoved = res.data;
    poslednyRezim = odpoved?.mode ?? null;
    poslednaChyba = null;
  } catch (err) {
    poslednaChyba = err?.response?.status
      ? `ticket HTTP ${err.response.status}`
      : `ticket: ${err?.message || "sietova chyba"}`;
    if (mojaGeneracia !== generacia) return;
    if (err?.response?.status === 401) {
      // Token vyprsal. Zamerne sa tu NEODHLASUJE -- vyrvalo by to pouzivatela
      // z rozpisaneho formulara. Najblizsie bezne API volanie to vyriesi.
      zrusCasovace();
      nastavStav("expired");
      return;
    }
    naplanujReconnect();
    return;
  }

  if (mojaGeneracia !== generacia || refcount === 0) return;

  if (odpoved?.pollMs) pollMs = odpoved.pollMs;

  if (odpoved?.mode === "off") {
    zrusCasovace();
    nastavStav("off");
    return;
  }

  if (odpoved?.mode !== "sse" || !odpoved.ticket) {
    // Server povedal 'poll' -- bud je vypnute SSE, alebo je hub plny.
    prepniNaPolling();
    return;
  }

  const zdroj = new EventSource(REALTIME_STREAM_URL(odpoved.ticket));

  // StrictMode: kym bezal ticket fetch, mohol vzniknut novy pokus o pripojenie.
  if (mojaGeneracia !== generacia) {
    zdroj.close();
    return;
  }
  es = zdroj;

  zdroj.onopen = () => {
    if (es !== zdroj) return;
    pokusov = 0;
    poslednaSprava = Date.now();
    nastavStav("open");
    spustiWatchdog();
    resync("connected");
  };

  zdroj.onmessage = (ev) => {
    if (es !== zdroj) return;
    poslednaSprava = Date.now();
    let data;
    try {
      data = JSON.parse(ev.data);
    } catch (err) {
      return; // nie je to udalost; cas prijatia je uz zaznamenany vyssie
    }
    // Heartbeat hovori len tolko, ze spojenie zije -- to uz je zaznamenane.
    // K obrazovkam sa dostat NESMIE: presiel by kazdym filtrom a spustil by
    // zbytocny refetch kazdych 20 sekund.
    if (data && data.entity === "heartbeat") return;
    emit(data);
  };

  zdroj.onerror = () => {
    if (es !== zdroj) return;
    poslednaChyba = "stream spadol (EventSource onerror)";
    zavriStream();
    clearInterval(watchdogTimer);
    watchdogTimer = null;
    nastavStav("connecting");
    naplanujReconnect();
  };
}

function odpoj() {
  generacia += 1;
  zavriStream();
  zrusCasovace();
  pokusov = 0;
  nastavStav("idle");
}

function onVisibility() {
  if (refcount === 0) return;
  if (document.visibilityState !== "visible") return;
  // Follower nema stream ani watchdog; dobiehaci resync si vypyta od lidra
  // tym, ze o nom povie -- staci lokalny resync, data si dotiahne sam.
  if (pouzivaLidra && !leader.isLeader()) {
    resync("visible");
    return;
  }
  // iOS Safari zabija SSE spojenia na pozadi. Toto je oprava pre DriverPage,
  // kde vodic prehliadac bezne minimalizuje.
  if (stav !== "polling" && (!es || es.readyState !== 1)) {
    zavriStream();
    pokusov = 0;
    naplanujReconnect(true);
  }
  resync("visible");
}

function onOnline() {
  if (refcount === 0) return;
  if (pouzivaLidra && !leader.isLeader()) {
    resync("online");
    return;
  }
  pokusov = 0;
  if (stav !== "polling") {
    zavriStream();
    naplanujReconnect(true);
  }
  resync("online");
}

function onOffline() {
  if (pouzivaLidra && !leader.isLeader()) return;
  zavriStream();
  clearTimeout(reconnectTimer);
  reconnectTimer = null;
  nastavStav("connecting");
}

let poslucacePripojene = false;
function pripojPoslucacov() {
  if (poslucacePripojene) return;
  poslucacePripojene = true;
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
}

function odpojPoslucacov() {
  if (!poslucacePripojene) return;
  poslucacePripojene = false;
  document.removeEventListener("visibilitychange", onVisibility);
  window.removeEventListener("online", onOnline);
  window.removeEventListener("offline", onOffline);
}

export function acquire() {
  clearTimeout(odpojitTimer);
  odpojitTimer = null;
  refcount += 1;
  if (refcount !== 1) return;

  pripojPoslucacov();

  pouzivaLidra = leader.isSupported();
  if (!pouzivaLidra) {
    // Bez BroadcastChannel/localStorage sa pripoji kazda zalozka zvlast --
    // presne ako pred zavedenim volby lidra.
    pripoj();
    return;
  }

  leader.start({
    onBecomeLeader: () => {
      pokusov = 0;
      // Povyseny follower mohol pocas prechodu nieco zmeskat.
      resync("promoted");
      pripoj();
    },
    onBecomeFollower: () => {
      // Stream drzi vzdy len lider.
      generacia += 1;
      zavriStream();
      zrusCasovace();
      nastavStav("open");
    },
    onMessage: (event) => emit(event, { rozposlat: false }),
  });
}

// Dev-only pristup z konzoly. Bez toho sa neda zmerat, kde presne vznika
// oneskorenie medzi zmenou a prekreslenim -- modul nie je nikde na window.
//
//   window.__realtime.stav()
//   window.__realtime.sleduj()   -> loguje kazdu udalost aj s jej latenciou
//
// V produkcnom builde sa cely blok odstrani (process.env.NODE_ENV je
// konstanta, takze to vyhodi dead-code elimination).
if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
  window.__realtime = {
    stav: () => ({
      stav,
      lider: pouzivaLidra ? leader.isLeader() : "volba lidra vypnuta",
      poslednaOdpovedTicketu: poslednyRezim,
      poslednaChyba,
      neuspesnychPokusov: pokusov,
      pollMs,
      sekundOdPoslednejSpravy: poslednaSprava ? Math.round((Date.now() - poslednaSprava) / 1000) : null,
      refcount,
      odberatelov: odberatelia.length,
    }),
    sleduj: () =>
      subscribe((e) => {
        const latencia = e.ts ? `${Date.now() - e.ts} ms od servera` : "lokalna";
        console.log(`[realtime] ${e.entity}/${e.action || e.reason || "-"} (${latencia})`, e);
      }),
    resync: forceResync,
  };
}

export function release() {
  refcount = Math.max(0, refcount - 1);
  if (refcount > 0) return;
  // Odklad prezije StrictMode dvojity mount aj bezne prepnutie obrazovky --
  // inak by sa spojenie pri kazdej navigacii zbytocne trhalo.
  clearTimeout(odpojitTimer);
  odpojitTimer = setTimeout(() => {
    if (refcount > 0) return;
    odpojPoslucacov();
    if (pouzivaLidra) {
      leader.stop();
      pouzivaLidra = false;
    }
    odpoj();
  }, ODKLAD_ODPOJENIA_MS);
}
